if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    /**
     * THE LIGHTBOX SHOWS ONE PHOTOGRAPH AND NOW LETS YOU MOVE BETWEEN THEM.
     * -------------------------------------------------------------------------
     * Two merchant requests that read as opposites and are not:
     *
     *   2026-08-11  "one photo, not the whole roll" — Dawn stacks every image in
     *               the modal and scrolls to the clicked one, so opening a
     *               photograph hands you a second gallery. section-product.css
     *               hides everything but `.active` and that stays.
     *   2026-08-21  "scroll through the images when the images are clicked and
     *               maximise".
     *
     * The first removed the stack; the second wants navigation. A lightbox is
     * supposed to have both — one image at a time, with a way forward and back.
     * Measured before this: 8 images in the modal, 7 at `display: none`,
     * `scrollHeight === clientHeight`. Seven photographs with no route to them.
     *
     * Three ways in, because the three input modes each expect their own:
     * arrows for the pointer, Left/Right for the keyboard, and a horizontal
     * swipe for touch — which is the one a phone reaches for first and the one
     * Dawn never had here.
     */
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.mediaItems = [];
        this.index = 0;
      }

      hide() {
        super.hide();
        this.teardownPaging();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
        this.setupPaging();
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`,
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }

      /* ---------------------------------------------------------------- *
       * Paging
       * ---------------------------------------------------------------- */

      setupPaging() {
        this.mediaItems = Array.from(this.querySelectorAll('[data-media-id]'));
        this.nav = this.querySelector('[data-media-nav]');
        if (!this.nav) return;

        // One photograph needs no controls. `hidden` rather than a class so it
        // is out of the accessibility tree too, not merely off screen.
        if (this.mediaItems.length < 2) {
          this.nav.hidden = true;
          return;
        }
        this.nav.hidden = false;

        this.index = Math.max(
          0,
          this.mediaItems.findIndex((el) => el.classList.contains('active')),
        );

        this.prevButton = this.nav.querySelector('[data-media-prev]');
        this.nextButton = this.nav.querySelector('[data-media-next]');
        this.counter = this.nav.querySelector('[data-media-counter]');

        this.onPrev = () => this.step(-1);
        this.onNext = () => this.step(1);
        this.prevButton.addEventListener('click', this.onPrev);
        this.nextButton.addEventListener('click', this.onNext);

        /* Bound on the modal, not the document: ModalDialog traps focus inside
           this element while it is open, so the keys can only arrive here, and
           a document listener would still be live after close. */
        this.onKey = (event) => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.step(-1);
          } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.step(1);
          }
        };
        this.addEventListener('keydown', this.onKey);

        this.bindSwipe();
        this.render();
      }

      teardownPaging() {
        this.prevButton?.removeEventListener('click', this.onPrev);
        this.nextButton?.removeEventListener('click', this.onNext);
        if (this.onKey) this.removeEventListener('keydown', this.onKey);
        this.unbindSwipe();
      }

      /* Wraps at both ends. A lightbox is a short loop, not a document — being
         stopped dead on the last photograph reads as a broken button, and there
         is no scrollbar here to tell you where you are. The counter carries the
         position instead. */
      step(delta) {
        if (this.mediaItems.length < 2) return;
        const next = (this.index + delta + this.mediaItems.length) % this.mediaItems.length;
        this.setActive(next);
      }

      setActive(index) {
        const item = this.mediaItems[index];
        if (!item) return;
        this.mediaItems[this.index]?.classList.remove('active');
        item.classList.add('active');
        this.index = index;

        // Deferred video has to be told to load when it becomes the active one,
        // exactly as `showActiveMedia` does for the image it opened on.
        const template = item.querySelector?.('template');
        if (item.nodeName === 'DEFERRED-MEDIA' && template?.content.querySelector('.js-youtube')) {
          item.loadContent();
        }
        this.render();
      }

      render() {
        if (!this.counter) return;
        this.counter.textContent = `${this.index + 1} / ${this.mediaItems.length}`;
      }

      /* SWIPE — horizontal only, and it must not fight a pinch-zoom or the
         vertical pan that scrolls a tall photograph. So: single pointer only,
         and the axis is decided once, by whichever delta passes the 10px
         threshold first. Below that nothing is committed, which is what stops a
         slightly-diagonal scroll from paging the image out from under a
         reader. */
      bindSwipe() {
        const surface = this.querySelector('[role="document"]');
        if (!surface) return;
        this.swipeSurface = surface;

        let startX = 0;
        let startY = 0;
        let axis = null;
        let pointerId = null;

        this.onPointerDown = (event) => {
          if (pointerId !== null || !event.isPrimary) return;
          pointerId = event.pointerId;
          startX = event.clientX;
          startY = event.clientY;
          axis = null;
        };

        this.onPointerMove = (event) => {
          if (event.pointerId !== pointerId) return;
          const dx = event.clientX - startX;
          const dy = event.clientY - startY;
          if (axis === null && Math.abs(dx) + Math.abs(dy) > 10) {
            axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
          }
        };

        this.onPointerUp = (event) => {
          if (event.pointerId !== pointerId) return;
          const dx = event.clientX - startX;
          pointerId = null;
          if (axis !== 'x' || Math.abs(dx) < 40) return;
          this.step(dx < 0 ? 1 : -1);
        };

        surface.addEventListener('pointerdown', this.onPointerDown);
        surface.addEventListener('pointermove', this.onPointerMove);
        surface.addEventListener('pointerup', this.onPointerUp);
        surface.addEventListener('pointercancel', this.onPointerUp);
      }

      unbindSwipe() {
        const surface = this.swipeSurface;
        if (!surface || !this.onPointerDown) return;
        surface.removeEventListener('pointerdown', this.onPointerDown);
        surface.removeEventListener('pointermove', this.onPointerMove);
        surface.removeEventListener('pointerup', this.onPointerUp);
        surface.removeEventListener('pointercancel', this.onPointerUp);
      }
    },
  );
}
